import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    TextField,
} from "@mui/material";

const ScanHistory = () => {
    const { partnerId } = useParams();
    const [partner, setPartner] = useState(null);
    const [loading, setLoading] = useState(true);
    const [locations, setLocations] = useState({});
    const [startDate, setStartDate] = useState(dayjs().subtract(30, "day").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));

    useEffect(() => {
        const fetchPartner = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/get-marketing-partner/${partnerId}`);
                setPartner(res.data);
            } catch (err) {
                console.error("Error fetching partner:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchPartner();
    }, [partnerId]);

    useEffect(() => {
        const fetchLocations = async () => {
            if (!partner?.totalScans?.length) return;

            const scansToSend = partner.totalScans.filter(scan => scan.latitude && scan.longitude);
            try {
                const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/get-scan-locations`, {
                    scans: scansToSend
                });

                setLocations(res.data);
            } catch (err) {
                console.error("Backend geocoding failed:", err);
            }
        };

        fetchLocations();
    }, [partner]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress sx={{ color: "#12c099" }} />
            </Box>
        );
    }

    const filteredScans = partner.totalScans?.filter(scan => {
        const scanDate = dayjs(scan.timestamp);
        return scanDate.isAfter(dayjs(startDate).subtract(1, 'day')) && scanDate.isBefore(dayjs(endDate).add(1, 'day'));
    }) || [];

    const groupByDate = (scans) => {
        const grouped = {};
        scans.forEach((scan) => {
            const date = new Date(scan.timestamp).toLocaleDateString();
            if (!grouped[date]) grouped[date] = [];
            grouped[date].push(scan);
        });
        return grouped;
    };

    const groupedScans = groupByDate(filteredScans);

    return (
        <Box sx={{ color: "#fff" }}>
            <Typography variant="h5" gutterBottom sx={{ color: "#12c099", fontWeight: 600 }}>
                Scan History – {partner.name}
            </Typography>

            <Typography variant="body2" sx={{ color: "#ccc", mb: 3 }}>
                This page shows a log of all QR code scans associated with ad campaigns from{" "}
                <Link
                    to={`/partner/${partnerId}`}
                    style={{ color: "#12c099", textDecoration: "underline" }}
                >
                    {partner.name}
                </Link>
                . Each entry includes the location and time of scan.
            </Typography>

            <Box display="flex" gap={2} mb={3}>
                <TextField
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ input: { color: "#fff" }, label: { color: "#ccc" }, backgroundColor: "#202020" }}
                />
                <TextField
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ input: { color: "#fff" }, label: { color: "#ccc" }, backgroundColor: "#202020" }}
                />
            </Box>

            <Card sx={{ backgroundColor: "#202020", mb: 3, p: 2, borderLeft: "4px solid #12c099", boxShadow: 1 }}>
                <CardContent sx={{ py: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: "#ccc" }}>
                        Total Scans (Filtered):{" "}
                        <span style={{ color: "#12c099", fontWeight: 500 }}>
                            {filteredScans.length}
                        </span>
                    </Typography>
                </CardContent>
            </Card>

            {Object.entries(groupedScans).map(([date, scans]) => (
                <Box key={date} mb={3}>
                    <Typography variant="subtitle1" sx={{ color: "#12c099", mb: 1 }}>
                        {date}
                    </Typography>
                    {scans.map((scan, idx) => {
                        const locKey = `${scan.latitude},${scan.longitude}`;
                        const locationText = scan.latitude && scan.longitude
                            ? locations[locKey] || `Loading location for ${scan.latitude.toFixed(5)}, ${scan.longitude.toFixed(5)}...`
                            : "Location Unavailable";

                        return (
                            <Card
                                key={scan._id || idx}
                                sx={{
                                    mb: 1.5,
                                    backgroundColor: "#202020",
                                    borderRadius: 2,
                                    borderLeft: "4px solid #12c099",
                                    boxShadow: 1,
                                }}
                            >
                                <CardContent sx={{ py: 1.5 }}>
                                    <Typography variant="body2" sx={{ color: "#12c099", mb: 0.5 }}>
                                        Location: {locationText}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#ccc" }}>
                                        Scanned at: {new Date(scan.timestamp).toLocaleTimeString()}
                                    </Typography>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
};

export default ScanHistory;
